/**
 * _shared.jsx — Kleine UI-Primitives die quer durch den CaseEditor
 * verwendet werden. Bewusst stateless / minimal — keine Closures, nur
 * Props. Erleichtert das Wiederverwenden in Subkomponenten.
 */
import React, { useEffect, useState } from "react";
import { CheckCircle2, Sparkle } from "lucide-react";
import { Label } from "@/components/ui/label";
import { validateVIN } from "@/lib/vinValidator";
import { validatePlate } from "@/lib/plateValidator";
import MicFieldButton from "@/components/voice/MicFieldButton";


export function StepHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-4 pb-4 border-b border-zinc-200">
      <div className="h-11 w-11 rounded-xl brand-gradient-soft flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-cyan-700" />
      </div>
      <div>
        <div className="font-display text-xl font-bold tracking-tight">{title}</div>
        <div className="text-sm text-zinc-500">{subtitle}</div>
      </div>
    </div>
  );
}


/**
 * Field mit optionaler Voice-Dictate-Integration.
 *
 * Props:
 *   label, children: wie gehabt
 *   highlight, autoFilled: Decorative Badges
 *   onVoiceAppend: (text) => void  → wenn gesetzt, erscheint ein
 *                                    Mikrofon-Icon neben dem Label.
 *                                    Aufnahme → Whisper-Transkript wird
 *                                    via Callback an den Parent gereicht.
 *   voiceTestid?: string           → optionaler test-id-suffix
 */
export function Field({ label, children, highlight, autoFilled, onVoiceAppend, voiceTestid, dense }) {
  return (
    <div className={autoFilled ? "relative" : undefined}>
      <div className={`flex items-center gap-1.5 ${dense ? "min-h-[24px]" : "min-h-[28px]"}`}>
        <Label className={`${dense ? "text-[10px] tracking-[0.16em]" : "text-xs tracking-[0.18em]"} uppercase font-semibold ${autoFilled ? "text-emerald-700" : "text-zinc-500"}`}>{label}</Label>
        {autoFilled ? (
          <span className="inline-flex items-center gap-1 text-[9px] font-bold tracking-widest text-emerald-700 bg-emerald-100 border border-emerald-300 rounded-full px-1.5 py-0.5 uppercase shadow-sm">
            <CheckCircle2 className="h-2.5 w-2.5" strokeWidth={2.6} /> Aus Scan
          </span>
        ) : highlight ? (
          <span className="inline-flex items-center gap-0.5 text-[8px] font-bold tracking-widest text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-1.5 py-0.5 uppercase">
            <Sparkle className="h-2.5 w-2.5" strokeWidth={2.2} /> Auto
          </span>
        ) : null}
        {onVoiceAppend && (
          <MicFieldButton
            onTranscript={onVoiceAppend}
            testid={voiceTestid || `mic-${(label || "field").toLowerCase().replace(/\s+/g, "-")}`}
            className={`ml-auto ${dense ? "h-5 w-5" : "h-6 w-6"}`}
          />
        )}
      </div>
      <div className={dense ? "mt-1" : "mt-1.5"}>{children}</div>
    </div>
  );
}


/**
 * Live VIN-Plausibilitätsprüfung — zeigt direkt unter dem Eingabefeld
 * Hersteller, Werk, Modelljahr und Checksummen-Validität. Offline (kein API-Call).
 */
export function VINLiveInfo({ vin }) {
  const info = validateVIN(vin);
  if (!vin || vin.length < 3) return null;

  const incomplete = vin.length > 0 && vin.length < 17;
  const stateColor = incomplete
    ? "border-amber-300 bg-amber-50 text-amber-800"
    : info.errors.length
    ? "border-red-300 bg-red-50 text-red-800"
    : "border-emerald-300 bg-emerald-50 text-emerald-900";

  return (
    <div
      data-testid="vin-live-info"
      className={`mt-2 text-xs border rounded-md px-2.5 py-1.5 ${stateColor} space-y-0.5`}
    >
      {incomplete ? (
        <div>📏 Noch {17 - vin.length} Zeichen für vollständige VIN</div>
      ) : info.errors.length ? (
        <div>⚠ {info.errors.join(" · ")}</div>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <span className="font-semibold">✓ VIN plausibel</span>
            {info.checksumValid === false && (
              <span className="text-amber-700">· Prüfziffer abweichend (bei EU-Kfz häufig)</span>
            )}
          </div>
          {info.make && (
            <div data-testid="vin-info-make">
              🚗 <span className="font-semibold">{info.make}</span> · {info.makeNote}
            </div>
          )}
          {info.year && (
            <div data-testid="vin-info-year">
              📅 Modelljahr: <span className="font-semibold">{info.year}</span>{" "}
              <span className="opacity-60">(Code „{info.normalized[9]}" → {info.yearRange})</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}


/**
 * Live Kennzeichen-Plausibilität — zeigt Stadt + Bundesland aus dem
 * Unterscheidungszeichen. Offline, kein API-Call.
 */
export function PlateLiveInfo({ plate }) {
  const info = validatePlate(plate);
  if (!plate || plate.length < 2) return null;

  const stateColor = info.valid
    ? "border-emerald-300 bg-emerald-50 text-emerald-900"
    : info.city
    ? "border-sky-300 bg-sky-50 text-sky-900"
    : "border-amber-300 bg-amber-50 text-amber-800";

  return (
    <div
      data-testid="plate-live-info"
      className={`mt-2 text-xs border rounded-md px-2.5 py-1.5 ${stateColor} space-y-0.5`}
    >
      {info.valid ? (
        <>
          <div className="font-semibold">✓ Kennzeichen plausibel</div>
          <div data-testid="plate-info-city">
            📍 <span className="font-semibold">{info.city}</span> · {info.state}
          </div>
        </>
      ) : info.city ? (
        <>
          <div>📍 <span className="font-semibold">{info.city}</span> · {info.state}</div>
          {info.reason && <div className="opacity-70">{info.reason}</div>}
        </>
      ) : (
        <div>⚠ {info.reason || "Format prüfen"}</div>
      )}
    </div>
  );
}


/**
 * Kleiner Status-Indikator neben dem Save-Button.
 *  - "saving"  → grauer Punkt + "Wird gespeichert…"
 *  - "saved"   → grüner Punkt + "Gespeichert · vor Xs"  (live updating)
 *  - "error"   → roter Punkt + "Speichern fehlgeschlagen"
 *  - "idle"    → nichts
 */
export function AutoSaveBadge({ status, lastSavedAt }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (status !== "saved" || !lastSavedAt) return;
    const t = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, [status, lastSavedAt]);

  if (status === "idle" && !lastSavedAt) return null;

  const ago = (() => {
    if (!lastSavedAt) return "";
    const sec = Math.max(0, Math.round((Date.now() - lastSavedAt) / 1000));
    if (sec < 5) return "gerade eben";
    if (sec < 60) return `vor ${sec}s`;
    if (sec < 3600) return `vor ${Math.round(sec / 60)} Min.`;
    return `vor ${Math.round(sec / 3600)} Std.`;
  })();

  let dot, label, cls;
  if (status === "saving") {
    dot = "bg-amber-400 animate-pulse";
    label = "Wird gespeichert …";
    cls = "text-amber-700";
  } else if (status === "error") {
    dot = "bg-rose-500";
    label = "Speichern fehlgeschlagen";
    cls = "text-rose-700";
  } else {
    dot = "bg-emerald-500";
    label = `Gespeichert · ${ago}`;
    cls = "text-emerald-700";
  }
  return (
    <div className={`hidden sm:inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full bg-zinc-50 ${cls}`} data-testid="autosave-badge" data-status={status}>
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </div>
  );
}
